import { ACTIONS, HTTP_STATUS_CODE } from "constants/constants";

export const ANNUAL_BALANCESHEET_UPLOAD_MAX_SIZE = 2 * 1024 * 1024;
export const ANNUAL_BALANCESHEET_UPLOAD_ACCEPT =
  ".pdf,.docx,.xlsx,.png,.jpg,.jpeg";
export const ANNUAL_FILE_ACTION = {
  UPLOAD: ACTIONS.UPLOAD,
  DELETE: ACTIONS.DELETE,
};
export const ANNUAL_FILE_UPLOAD_VALIDATION_MESSAGE = {
  FILE_TOO_LARGE:
    "Le fichier est trop volumineux. La taille maximale autorisée est de 2 Mo.",
  INVALID_EXTENSION:
    "Format de fichier non autorisé. Formats acceptés : PDF, DOCX, XLSX, PNG, JPG, JPEG.",
};

const ANNUAL_BALANCESHEET_ALLOWED_EXTENSIONS = [
  "pdf",
  "docx",
  "xlsx",
  "png",
  "jpg",
  "jpeg",
];

const ANNUAL_UPLOAD_ERROR_CODE = {
  FILE_TOO_LARGE: "file-too-large",
  FORBIDDEN_EXTENSION: "forbidden-extension",
  CAN_NOT_READ_FILE_EXTENSION: "can-not-read-file-extension",
  FIELD_IS_NOT_OF_TYPE_FILE: "field-is-not-of-type-file",
};

const ANNUAL_LINK_ERROR_STATUSES = [
  HTTP_STATUS_CODE.UNAUTHORIZED,
  HTTP_STATUS_CODE.RESTRICTED_ACCESS,
];

const ANNUAL_INVALID_EXTENSION_ERROR_CODES = [
  ANNUAL_UPLOAD_ERROR_CODE.FORBIDDEN_EXTENSION,
  ANNUAL_UPLOAD_ERROR_CODE.CAN_NOT_READ_FILE_EXTENSION,
];
const ANNUAL_FILE_UPLOAD_ERROR_MESSAGE = {
  LINK_INVALID:
    "Le lien de votre bilan annuel n'est plus valide. Contactez votre manager pour obtenir un nouveau lien.",
  DELETE_FAILED: "Le fichier n'a pas pu être supprimé. Veuillez réessayer.",
  INVALID_TARGET_FIELD:
    "Le champ ciblé n'accepte pas de fichier. Veuillez recharger la page puis réessayer.",
  UPLOAD_FAILED:
    "Le fichier n'a pas pu être téléversé. Vérifiez son contenu puis réessayez.",
};
const ANNUAL_FILE_UPLOAD_MODAL_MESSAGES = {
  [ANNUAL_FILE_ACTION.UPLOAD]: {
    pendingTitleId: "annual_balancesheet_upload_pending_title",
    pendingTitleDefault: "Téléversement en cours...",
    pendingMessageId: "annual_balancesheet_upload_pending_message",
    pendingMessageDefault: "Votre fichier est en cours d'envoi. Veuillez patienter.",
    successTitleId: "annual_balancesheet_upload_success_title",
    successTitleDefault: "Fichier ajouté",
    successMessageId: "annual_balancesheet_upload_success_message",
    successMessageDefault: "Le fichier a bien été ajouté au bilan annuel.",
    errorTitleId: "annual_balancesheet_upload_error_title",
    errorTitleDefault: "Échec du téléversement",
    errorMessageId: "annual_balancesheet_upload_error_message",
    errorMessageDefault:
      "Le fichier n'a pas pu être téléversé. Veuillez réessayer.",
  },
  [ANNUAL_FILE_ACTION.DELETE]: {
    pendingTitleId: "annual_balancesheet_delete_pending_title",
    pendingTitleDefault: "Suppression en cours...",
    pendingMessageId: "annual_balancesheet_delete_pending_message",
    pendingMessageDefault:
      "Le fichier est en cours de suppression. Veuillez patienter.",
    successTitleId: "annual_balancesheet_delete_success_title",
    successTitleDefault: "Fichier supprimé",
    successMessageId: "annual_balancesheet_delete_success_message",
    successMessageDefault: "Le fichier a bien été supprimé du formulaire.",
    errorTitleId: "annual_balancesheet_delete_error_title",
    errorTitleDefault: "Échec de la suppression",
    errorMessageId: "annual_balancesheet_delete_error_message",
    errorMessageDefault: ANNUAL_FILE_UPLOAD_ERROR_MESSAGE.DELETE_FAILED,
  },
};

const getAnnualUploadErrorCode = (error) =>
  [
    error?.response?.data,
    error?.response?.data?.code,
    error?.response?.data?.error,
    error?.response?.data?.message,
    error?.response?.data?.errors?.[0]?.code,
    error?.response?.data?.errors?.[0]?.message,
  ].find((value) => typeof value === "string");

export const getAnnualUploadErrorMessage = (
  error,
  actionType = ANNUAL_FILE_ACTION.UPLOAD
) => {
  const status = error?.response?.status;
  const errorCode = getAnnualUploadErrorCode(error);

  if (ANNUAL_LINK_ERROR_STATUSES.includes(status)) {
    return ANNUAL_FILE_UPLOAD_ERROR_MESSAGE.LINK_INVALID;
  }

  if (actionType === ANNUAL_FILE_ACTION.DELETE) {
    return ANNUAL_FILE_UPLOAD_ERROR_MESSAGE.DELETE_FAILED;
  }

  if (
    status === HTTP_STATUS_CODE.PAYLOAD_TOO_LARGE ||
    errorCode === ANNUAL_UPLOAD_ERROR_CODE.FILE_TOO_LARGE
  ) {
    return ANNUAL_FILE_UPLOAD_VALIDATION_MESSAGE.FILE_TOO_LARGE;
  }

  if (ANNUAL_INVALID_EXTENSION_ERROR_CODES.includes(errorCode)) {
    return ANNUAL_FILE_UPLOAD_VALIDATION_MESSAGE.INVALID_EXTENSION;
  }

  if (errorCode === ANNUAL_UPLOAD_ERROR_CODE.FIELD_IS_NOT_OF_TYPE_FILE) {
    return ANNUAL_FILE_UPLOAD_ERROR_MESSAGE.INVALID_TARGET_FIELD;
  }

  return ANNUAL_FILE_UPLOAD_ERROR_MESSAGE.UPLOAD_FAILED;
};

export const getAnnualFileUploadModalMessages = (
  fileActionType,
  fileUploadErrorMessage
) => {
  const modalMessages =
    ANNUAL_FILE_UPLOAD_MODAL_MESSAGES[fileActionType] ||
    ANNUAL_FILE_UPLOAD_MODAL_MESSAGES[ANNUAL_FILE_ACTION.UPLOAD];

  return {
    ...modalMessages,
    errorMessageDefault:
      fileUploadErrorMessage || modalMessages.errorMessageDefault,
  };
};

export const isAnnualUploadFileValid = (file) => {
  const extension = String(file?.name || "")
    .split(".")
    .pop()
    .toLowerCase();

  return ANNUAL_BALANCESHEET_ALLOWED_EXTENSIONS.includes(extension);
};
